import React, { useState, useEffect } from 'react';
import { Photo } from '../types/photo';
import { getApartmentPhotos, deletePhoto } from '../lib/photoStorage';
import { Camera, Trash2, Download, Tag, MapPin, FileText, User, Calendar, X, Loader, ZoomIn, Link, ExternalLink, Navigation, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface PhotoGalleryProps {
  apartmentId: string;
  buildingId: string;
  onAddPhotos: () => void;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  apartmentId,
  buildingId,
  onAddPhotos,
}) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    loadPhotos();
  }, [apartmentId]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // If apartmentId is empty, try to get building photos instead
      let apartmentPhotos: Photo[] = [];
      
      if (apartmentId) {
        apartmentPhotos = await getApartmentPhotos(apartmentId);
      } else if (buildingId) {
        // This would be for building-level photos
        // Not implemented yet, but we'll handle it gracefully
        console.log("No apartment ID provided, would load building photos");
        apartmentPhotos = [];
      }
      
      setPhotos(apartmentPhotos);
    } catch (err) {
      console.error('Error loading photos:', err);
      setError('Fehler beim Laden der Fotos');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm('Möchten Sie dieses Foto wirklich löschen?')) {
      return;
    }

    try {
      setDeleting(photoId);
      await deletePhoto(photoId);
      setPhotos(photos.filter(photo => photo.id !== photoId));
      
      // Close the detail view if the deleted photo was selected
      if (selectedPhoto?.id === photoId) {
        setSelectedPhoto(null);
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      alert('Fehler beim Löschen des Fotos');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadPhoto = (photo: Photo) => {
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = photo.url;
    link.download = photo.fileName.split('/').pop() || 'photo.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openOneDriveLink = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const openGoogleMaps = (latitude: number, longitude: number) => {
    window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600">Fotos werden geladen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
        <p>{error}</p>
        <button 
          onClick={loadPhotos}
          className="mt-2 text-sm text-red-700 underline"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header with add button */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Fotos {photos.length > 0 && `(${photos.length})`}
        </h3>
        <button
          onClick={onAddPhotos}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Camera className="w-4 h-4" />
          <span>Fotos hinzufügen</span>
        </button>
      </div>

      {/* Photo grid */}
      {photos.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Keine Fotos vorhanden</p>
          <button
            onClick={onAddPhotos}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Camera className="w-5 h-5" />
            <span>Fotos hinzufügen</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {photos.map(photo => (
            <div 
              key={photo.id} 
              className="relative group cursor-pointer rounded-lg overflow-hidden bg-gray-100"
              onClick={() => setSelectedPhoto(photo)}
            >
              <img 
                src={photo.thumbnailUrl} 
                alt={photo.description || photo.notes || 'Foto'} 
                className="w-full h-32 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              {deleting === photo.id && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <Loader className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black to-transparent text-white text-xs truncate">
                {format(photo.createdAt, 'dd.MM.yyyy', { locale: de })}
              </div>
              {photo.oneDriveUrl && (
                <div className="absolute top-2 right-2 bg-blue-600 rounded-full p-1">
                  <Link className="w-3 h-3 text-white" />
                </div>
              )}
              {photo.gpsLocation && (
                <div className="absolute top-2 left-2 bg-green-600 rounded-full p-1">
                  <Navigation className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Photo detail modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Foto Details</h3>
              <button 
                onClick={() => setSelectedPhoto(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
              {/* Image */}
              <div className="md:w-2/3 bg-gray-900 flex items-center justify-center p-2 overflow-hidden">
                <img 
                  src={selectedPhoto.url} 
                  alt={selectedPhoto.description || selectedPhoto.notes || 'Foto'} 
                  className="max-w-full max-h-[60vh] object-contain"
                />
              </div>
              
              {/* Info */}
              <div className="md:w-1/3 p-4 overflow-y-auto">
                {/* Metadata */}
                <div className="space-y-4">
                  {/* Description */}
                  {selectedPhoto.description && (
                    <div className="flex items-start gap-2">
                      <FileText className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Beschreibung</p>
                        <p className="text-sm text-gray-600 whitespace-pre-line">{selectedPhoto.description}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* OneDrive Link */}
                  {selectedPhoto.oneDriveUrl && (
                    <div className="flex items-start gap-2">
                      <Link className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">OneDrive Link</p>
                        <button
                          onClick={() => openOneDriveLink(selectedPhoto.oneDriveUrl!)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <span>In OneDrive öffnen</span>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* GPS Location */}
                  {selectedPhoto.gpsLocation && (
                    <div className="flex items-start gap-2">
                      <Navigation className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">GPS-Standort</p>
                        <p className="text-sm text-gray-600">
                          {selectedPhoto.gpsLocation.latitude.toFixed(6)}, {selectedPhoto.gpsLocation.longitude.toFixed(6)}
                        </p>
                        {selectedPhoto.gpsLocation.address && (
                          <p className="text-sm text-gray-600 mt-1">{selectedPhoto.gpsLocation.address}</p>
                        )}
                        <button
                          onClick={() => openGoogleMaps(selectedPhoto.gpsLocation!.latitude, selectedPhoto.gpsLocation!.longitude)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                        >
                          <span>In Google Maps öffnen</span>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Timestamp */}
                  {selectedPhoto.timestamp && (
                    <div className="flex items-start gap-2">
                      <Clock className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Zeitstempel</p>
                        <p className="text-sm text-gray-600">
                          {format(selectedPhoto.timestamp, 'dd. MMMM yyyy, HH:mm:ss', { locale: de })}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Date and user */}
                  <div className="flex items-start gap-2">
                    <Calendar className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Aufnahmedatum</p>
                      <p className="text-sm text-gray-600">
                        {format(selectedPhoto.createdAt, 'dd. MMMM yyyy, HH:mm', { locale: de })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Location */}
                  {selectedPhoto.location && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Ort/Raum</p>
                        <p className="text-sm text-gray-600">{selectedPhoto.location}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Tags */}
                  {selectedPhoto.tags && selectedPhoto.tags.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Tag className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Tags</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedPhoto.tags.map((tag, index) => (
                            <span 
                              key={index}
                              className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Notes */}
                  {selectedPhoto.notes && (
                    <div className="flex items-start gap-2">
                      <FileText className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Notizen</p>
                        <p className="text-sm text-gray-600 whitespace-pre-line">{selectedPhoto.notes}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Technical info */}
                  <div className="pt-4 border-t">
                    <p className="text-xs text-gray-500">
                      Dateigröße: {(selectedPhoto.fileSize / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <p className="text-xs text-gray-500">
                      Typ: {selectedPhoto.mimeType}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer with actions */}
            <div className="flex justify-between items-center p-4 border-t">
              <button
                onClick={() => handleDeletePhoto(selectedPhoto.id)}
                className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                disabled={deleting === selectedPhoto.id}
              >
                {deleting === selectedPhoto.id ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                <span>Löschen</span>
              </button>
              
              <button
                onClick={() => handleDownloadPhoto(selectedPhoto)}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Herunterladen</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};